import axios from 'axios';
import { STORAGE_KEYS } from '../constants';

const API_BASE_URL = process.env.REACT_APP_API_URL || '';

const api = axios.create({
  baseURL: API_BASE_URL,
  // No timeout - requests will wait until server responds or connection closes
});

// Request interceptor
api.interceptors.request.use(
  (config) => {
    // Rewrite deprecated endpoint so old frontend builds still work (no DB cache table)
    if (config.url && config.url.includes('/api/barangay/pets-by-barangay-cached')) {
      config.url = config.url.replace('/api/barangay/pets-by-barangay-cached', '/api/barangay/barangays/optimized');
      if (config.params && config.params.limit != null) {
        const n = Number(config.params.limit);
        if (!Number.isNaN(n) && n > 50) config.params.limit = 50;
      }
    }
    const accessToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (accessToken) {
      config.headers.Authorization = `Bearer ${accessToken}`;
    }

    // `skipOrgInjection` lets a caller fully control the org scope of a request
    // (e.g. prefetching another clinic's dashboard bundle to warm its cache
    // before the user switches to it). When set, we do NOT overwrite the org
    // params from localStorage — the caller's explicit params win.
    if (!config.skipOrgInjection) {
      const orgIdsStr = localStorage.getItem(STORAGE_KEYS.ORGANIZATION_IDS);
      if (orgIdsStr) {
        try {
          const organizationIds = JSON.parse(orgIdsStr);
          if (organizationIds?.length > 0) {
            if (!config.params) config.params = {};
            // Only inject when the caller hasn't supplied ids explicitly.
            if (config.params.organization_ids === undefined) {
              config.params.organization_ids = organizationIds.join(',');
            }
          }
        } catch (error) {
          console.error('Error parsing organization IDs:', error);
        }
      }

      // Attach org type/city from localStorage (written by AuthContext.login / applyOrgIdentity).
      // Only inject when the caller has not already supplied these params explicitly.
      const orgType = localStorage.getItem(STORAGE_KEYS.ORGANIZATION_TYPE);
      const orgCity = localStorage.getItem(STORAGE_KEYS.ORGANIZATION_CITY);
      if (!config.params) config.params = {};
      if (config.params.organization_type === undefined && orgType) {
        config.params.organization_type = orgType;
      }
      if (config.params.organization_city === undefined && orgCity) {
        config.params.organization_city = orgCity;
      }
    }
    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

// Heroku Eco/Basic dynos sleep after 30 min of inactivity and can take 10-25s to
// wake up, during which Heroku returns 503 with no CORS headers (browser sees
// ERR_NETWORK). Three retries with escalating delays cover the full wake-up window.
const RETRY_DELAYS_MS = [8000, 15000, 25000];
const MAX_RETRIES = 3;

// Response interceptor
api.interceptors.response.use(
  (response) => {
    return response;
  },
  (error) => {
    const is503OrNetwork = error.response?.status === 503 || error.code === 'ERR_NETWORK' || error.message === 'Network Error';
    const retried = error.config?.__retryCount ?? 0;

    if (is503OrNetwork && retried < MAX_RETRIES && error.config) {
      const delayMs = RETRY_DELAYS_MS[retried] ?? 4000;
      error.config.__retryCount = retried + 1;
      console.warn(`API request failed (503/network). Retry ${retried + 1}/${MAX_RETRIES} in ${delayMs / 1000}s...`);
      return new Promise((resolve, reject) => {
        setTimeout(() => {
          api.request(error.config).then(resolve).catch(reject);
        }, delayMs);
      });
    }

    if (is503OrNetwork) {
      console.warn('API request failed (service unavailable or network). Check that the analytics server is running.');
    } else {
      console.error('API Error:', error.response?.data || error.message);
    }

    if (error.response?.status === 401) {
      const currentPath = window.location.pathname;
      const hasToken = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
      const hasStoredAuth = localStorage.getItem(STORAGE_KEYS.AUTH) === 'true';
      if (currentPath !== '/' && currentPath !== '/login' && (hasToken || !hasStoredAuth)) {
        console.warn('Unauthorized - redirecting to login');
        Object.values(STORAGE_KEYS).forEach((key) => localStorage.removeItem(key));
        window.location.href = '/';
      }
    }
    
    return Promise.reject(error);
  }
);

// Pet-related API calls
export const petsAPI = {
  getCounts: (params) => api.get('/api/pets/counts', { params }),
  getByCity: (params) => api.get('/api/pets/by-city', { params }),
  getBySpecies: () => api.get('/api/pets/by-species'),
  getBySpeciesDetail: (params) => api.get('/api/pets/by-species-detail', { params }),
  getRegistrationStatus: () => api.get('/api/pets/registration-status'),
};

// Clinical Intelligence API calls
export const clinicalIntelligenceAPI = {
  getOverview: (params = {}) => api.get('/api/clinical-intelligence/overview', { params }),
  getTrends: () => api.get('/api/clinical-intelligence/trends'),
  getGeographic: () => api.get('/api/clinical-intelligence/geographic'),
  getDemographics: (params = {}) => api.get('/api/clinical-intelligence/demographics', { params }),
  getDiseases: () => api.get('/api/clinical-intelligence/diseases'),
  getComprehensiveAnalytics: (params) => api.get('/api/clinical-intelligence/comprehensive-analytics', { params }),
  getSimpleSpecies: () => api.get('/api/clinical-intelligence/simple-species'),
  getEnhancedAnalytics: (params) => api.get('/api/clinical-intelligence/enhanced-analytics', { params }),
  getYesterdayPatients: () => api.get('/api/clinical-intelligence/yesterday-patients'),
  getPetsPerBarangay: (params = {}) => api.get('/api/clinical-intelligence/pets-per-barangay', { params }),
  getRevenueAnalytics: (params = {}) => api.get('/api/clinical-intelligence/revenue-analytics', { params }),
  getDoseAnalytics: (params = {}) => api.get('/api/clinical-intelligence/dose-analytics', { params }),
  getPopulationGrowth: (params = {}) => api.get('/api/clinical-intelligence/population-growth', { params }),
};

// Legacy alias for backward compatibility
export const statisticsAPI = clinicalIntelligenceAPI;

// Search API calls
export const searchAPI = {
  searchPets: (params) => api.get('/api/search/pets', { params }),
  searchCities: (params) => api.get('/api/search/cities', { params }),
  searchSpecies: (params) => api.get('/api/search/species', { params }),
  searchSpeciesDetail: (params) => api.get('/api/search/species-detail', { params }),
  getSuggestions: (params) => api.get('/api/search/suggestions', { params }),
};

// Reminders API calls
export const remindersAPI = {
  getOverview: () => api.get('/api/reminders/overview'),
  getMonthly: (params = {}) => api.get('/api/reminders/monthly', { params }),
  /** Dashboard monthly statistics (alias for getMonthly with no params). */
  getMonthlyStatistics: () => api.get('/api/reminders/monthly', { params: {} }),
  getYearly: (params) => api.get('/api/reminders/yearly', { params }),
  getBreakdownByVaccinationType: (params) => api.get('/api/reminders/breakdown-by-vaccination-type', { params }),
  /** Total Records Encoded KPI — count of schema.appointments rows (optionally date-scoped). */
  getRecordsEncoded: (params = {}) => api.get('/api/reminders/records-encoded', { params }),
};

// Pet Ownership API calls
export const petOwnershipAPI = {
  getOverview: () => api.get('/api/pet-ownership/overview'),
  getHouseholds: () => api.get('/api/pet-ownership/households'),
  getSpeciesBreakdown: (params = {}) => api.get('/api/pet-ownership/species-breakdown', { params }),
  getGenderBreakdown: (params = {}) => api.get('/api/pet-ownership/gender-breakdown', { params }),
  getRegistrationStatus: (params = {}) => api.get('/api/pet-ownership/registration-status', { params }),
  getSpayNeuterStatus: (params = {}) => api.get('/api/pet-ownership/spay-neuter-status', { params }),
  getComprehensive: (params) => api.get('/api/pet-ownership/comprehensive', { params }),
  getTrends: (params) => api.get('/api/pet-ownership/trends', { params }),
  getGeographic: () => api.get('/api/pet-ownership/geographic'),
};

// Vaccination API calls
export const vaccinationAPI = {
  getOverview: () => api.get('/api/vaccination/overview'),
  getCompliance: (params = {}) => api.get('/api/vaccination/compliance', { params }),
  getBreakdown: () => api.get('/api/vaccination/breakdown'),
  getBySpecies: () => api.get('/api/vaccination/by-species'),
  getTrends: (params) => api.get('/api/vaccination/trends', { params }),
  getGeographic: () => api.get('/api/vaccination/geographic'),
};

// Dashboard bundle: one request for overview + pet-ownership + reminders + vaccination (reduces round-trips)
export const dashboardAPI = {
  getBundle: (params = {}) => api.get('/api/dashboard/bundle', { params }),
  /** Single request for Clinical Intelligence tab: overview, yesterday, vaccination, compliance, demographics, etc. */
  getClinicalIntelligenceBundle: (params = {}) => api.get('/api/dashboard/clinical-intelligence-bundle', { params }),
  /**
   * Warm the server-side bundle cache for a specific org WITHOUT changing the
   * active clinic. `params` must be built to match exactly what the real
   * post-switch fetch will send (see clinicBundleParams in SwitchClinicModal),
   * so the reload after switching hits a warm cache instead of recomputing.
   * Uses skipOrgInjection so the interceptor doesn't clobber these params with
   * the currently-active clinic's scope.
   */
  prefetchBundle: (params = {}) =>
    api.get('/api/dashboard/bundle', { params, skipOrgInjection: true }),
};

// Geographic API calls
export const geographicAPI = {
  geocodeAddress: (address, saveToDb = false) => api.get('/api/geographic/geocode-address', { 
    params: { address, save_to_db: saveToDb } 
  }),
  reverseGeocode: (latitude, longitude) => api.get('/api/geographic/reverse-geocode', { 
    params: { latitude, longitude } 
  }),
  searchPlaces: (query, latitude = null, longitude = null, radius = 50000) => api.get('/api/geographic/search-places', { 
    params: { query, latitude, longitude, radius } 
  }),
  getPlaceDetails: (placeId) => api.get('/api/geographic/place-details', { 
    params: { place_id: placeId } 
  }),
  getGeographicAnalytics: () => api.get('/api/geographic/geographic-analytics'),
  getMapData: (limit = 100, params = {}) => api.get('/api/geographic/map-data', { 
    params: { limit, ...params } 
  }),
  getCachedGeocodedAddresses: () => api.get('/api/geographic/cached-geocoded-addresses'),
  getHeatmapData: (limit = 1000, params = {}) => api.get('/api/geographic/heatmap-data', {
    params: { limit, ...params }
  }),
  getDebugCounts: () => api.get('/api/geographic/debug-counts'),
};

// Barangay Analytics API calls
export const barangayAPI = {
  /** Lightweight barangay-name list for the org's city (GeoJSON only, no counts). */
  getBarangayList: (params = {}) => api.get('/api/barangay/list', { params }),
  /** Regex + GeoJSON extraction (no DB cache table) */
  getPetsByBarangayLocal: (params = {}) => api.get('/api/barangay/pets-by-barangay-local', { params }),
  /** Faster endpoint (DB regex + cache, 2-5s or <500ms cached). Use for Clinical Intelligence to avoid 503. */
  getBarangaysOptimized: (params = {}) => api.get('/api/barangay/barangays/optimized', { params }),
  getBarangayDetails: (barangayName, params = {}) => api.get(`/api/barangay/barangay-details/${barangayName}`, { params }),
  getExtractionStats: () => api.get('/api/barangay/barangay-extraction-stats'),
  /** Per-barangay vaccination coverage gap and campaign planning metrics (5-min cache). */
  getCoverageGap: (params = {}) => api.get('/api/barangay/coverage-gap', { params }),
  /** Full simplified barangay boundary GeoJSON (browser-cached 24 h). */
  // `v` changes the URL when the boundary file changes, so a 24h browser cache
  // of an older (or empty) city partition cannot hide newly added polygons.
  getGeoJsonBoundaries: () => api.get('/api/barangay/geojson-boundaries', { params: { v: '20261006-lipa' } }),
  /**
   * City-specific barangay boundary GeoJSON — only features whose city property
   * matches cityName.  Typically < 200 KB vs ~1.8 MB for the full file.
   * Falls back to getGeoJsonBoundaries() when cityName is empty.
   */
  getGeoJsonBoundariesByCity: (cityName) =>
    cityName
      ? api.get(`/api/barangay/geojson-boundaries/city/${encodeURIComponent(cityName)}`, { params: { v: '20261006-lipa' } })
      : api.get('/api/barangay/geojson-boundaries', { params: { v: '20261006-lipa' } }),
  /**
   * Barangay boundary features by name (any city). Used to render barangays that
   * have data but fall outside the org's city partition on the Coverage Gap map.
   * @param {string[]} names - barangay names to fetch boundaries for.
   */
  getGeoJsonBoundariesByNames: (names = []) =>
    api.get('/api/barangay/geojson-boundaries/by-names', {
      params: { names: (names || []).join(','), v: '20261006-lipa' },
    }),
  /** Real vaccinator count + observed daily capacity for campaign duration estimates. */
  getCampaignCapacity: (params = {}) => api.get('/api/barangay/campaign-capacity', { params }),
};

// Vaccinator Activity API calls
export const vaccinatorActivityAPI = {
  getMonthlyLeaderboard: (params = {}) => api.get('/api/vaccinator-activity/monthly-leaderboard', { params }),
  getStats: (params = {}) => api.get('/api/vaccinator-activity/stats', { params }),
};

// Raw data export (single Excel workbook with Owners + Pets sheets)
export const exportAPI = {
  downloadRaw: (params = {}) =>
    api.get('/api/export/raw', { params, responseType: 'blob' }),
  /** Build the raw export server-side and email it to the user (no download). */
  emailRaw: (params = {}) =>
    api.get('/api/export/raw', { params: { ...params, email_only: true } }),
  getRabiesVaccinationReport: (params = {}) =>
    api.get('/api/export/rabies-vaccination-report', { params }),
  /**
   * Ask the backend for a presigned S3 PUT URL for a report PDF.
   * Returns { s3_available, url?, key?, content_type? }.
   */
  presignReportUpload: (formData) =>
    api.post('/api/export/email-report/presign', formData),
  /**
   * Upload the PDF blob straight to S3 (bypasses the web dyno's fragile upload
   * path). Uses the bare axios instance so our auth/org interceptors don't add
   * headers that would break the presigned signature. Bounded timeout + one retry.
   */
  uploadToS3: async (url, blob, contentType = 'application/pdf') => {
    const put = () =>
      axios.put(url, blob, {
        headers: { 'Content-Type': contentType },
        timeout: 120000,
        // Send the blob as-is; don't let axios JSON-stringify it.
        transformRequest: [(data) => data],
      });
    try {
      return await put();
    } catch (err) {
      // One retry for transient network hiccups before giving up.
      return await put();
    }
  },
  emailReport: (formData) =>
    api.post('/api/export/email-report', formData),
};

// Auth / organization API calls
export const authAPI = {
  /** List the veterinary organizations the given professional belongs to (clinic switcher). */
  getMyOrganizations: (professionalId) =>
    api.get('/api/auth/my-organizations', { params: { professional_id: professionalId } }),
  /**
   * List the child clinics of a parent organization (government dashboard sub-org
   * filter). Returns { is_parent, sub_organizations: [{ organization_id, organization_name }] }.
   */
  getSubOrganizations: (organizationId) =>
    api.get('/api/auth/sub-organizations', { params: { organization_id: organizationId } }),
  /**
   * Re-verify the logged-in user's password (gates report downloads).
   * Returns { valid: boolean }. In dev/staging bypass stacks any non-empty
   * password is accepted server-side.
   */
  verifyPassword: ({ password, professionalId, username } = {}) =>
    api.post('/api/auth/verify-password', {
      password,
      professional_id: professionalId,
      username,
    }),
};

// Health check
export const healthAPI = {
  check: () => api.get('/health'),
};

// Pre-warm: fire a lightweight health ping on first import so the Heroku dyno
// starts waking up before the user triggers any heavy data requests.
(() => {
  try { api.get('/health').catch(() => {}); } catch {}
})();

export default api;
